/**
 * Sliding Puzzle Quest — domain helpers on top of {@link ../pages/puzzles/sliding/SlidingService}.
 * @see ../../../docs/home-sliding-mobile-api.md
 */
import { templateForGridAndLevel } from "../engine/slidingTemplateUtils";
import {
    getSlidingGroupStoryId,
    getSlidingStoryTitle,
    pickSlidingPlayImageUrl,
    pickSlidingOriginalImageUrl,
    pickSlidingStoryIcon200Url,
} from "../../../../pages/browse-sliding/slidingBrowseUtils";
import SlidingService from "../../../../pages/puzzles/sliding/SlidingService";
import AuthService from "../../../../services/AuthService";
import Logger from "../../../../services/Logger";
import { getContentLanguage } from "../../../../services/LangPrefs";
import UtilService from "../../../../services/UtilService";
import { DIFFICULTY_LABELS } from "../routes";

const DIFF_TO_LEVEL = { easy: "EASY", medium: "MEDIUM", hard: "HARD" };
const LEVEL_TO_DIFF = { EASY: "easy", MEDIUM: "medium", HARD: "hard" };

/** Published template used for App Store snapshot play + library fixture (matches iOS StoreKit / native bootstrap). */
export const SNAPSHOT_SLIDING_TEMPLATE_ID = "8aff82be-57f6-473c-b1a4-0c4767bd818b";

const SNAPSHOT_STORY_ID = "6ca84ac2-3368-4890-b65f-a932afc10ea3";
const SNAPSHOT_STORY_TITLE = "The Goalkeeper's Secret";
const SNAPSHOT_IMAGE_URL =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/hidden_message/6ca84ac2-3368-4890-b65f-a932afc10ea3/eccb97a5-62c4-46b3-8b87-826c86565abb.png";
const SNAPSHOT_PREVIEW_URL =
    "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/hidden_message/6ca84ac2-3368-4890-b65f-a932afc10ea3/d0e9e0f2-eb14-4b8c-ac17-21f9371217bd.png";
const SNAPSHOT_STARTING_LAYOUT = [15, 5, 1, 2, 6, 9, 3, 7, 13, 0, 10, 11, 14, 4, 8, 12];

/** Category filter pre-selected for App Store library screenshots. */
export const SNAPSHOT_LIBRARY_SELECTED_TAG = "business";

/** Library category pills during Fastlane snapshots (no "All" chip). */
export const SNAPSHOT_LIBRARY_TAGS = [
    "business",
    "capital",
    "celebrity",
    "common",
    "plant",
    "funny",
];

/** Offline library category chips for Fastlane snapshots. */
export function getSnapshotLibraryTags() {
    return [...SNAPSHOT_LIBRARY_TAGS];
}

/** Per-tag offline library fixtures — distinct story art for App Store tag screenshots. */
const SNAPSHOT_LIBRARY_FIXTURES = {
    business: {
        storyId: SNAPSHOT_STORY_ID,
        title: SNAPSHOT_STORY_TITLE,
        imageIcon400Url: SNAPSHOT_IMAGE_URL,
        imageUrl: SNAPSHOT_PREVIEW_URL,
    },
    capital: {
        storyId: "e510efc5-6d5c-47cc-b86f-c19f94de977c",
        title: "The Viking's Last Saga",
        imageIcon400Url:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-preview/word_search/2489fe11-fa96-4469-b84c-cc3791f19423/98043074-0bb7-4af9-b327-eb79c03e843b.png",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/word_search/2489fe11-fa96-4469-b84c-cc3791f19423/3c43cb5d-5c2f-4d62-b428-6af63179a313.png",
    },
    celebrity: {
        storyId: "13bfc21e-9660-4305-a072-42a46bba9670",
        title: "Queen of the Screen",
        imageIcon400Url:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-preview/hidden_message/86c071c7-ec14-427b-99e1-f893306b7534/986d1847-f369-4bfd-bcc9-2825e017ea91.png",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/hidden_message/86c071c7-ec14-427b-99e1-f893306b7534/bd083930-6244-4125-a661-45b0ca19de5d.png",
    },
    common: {
        storyId: "5bcd6b55-b2e6-46c2-913b-7bbb47e4d216",
        title: "The Coffee Shop Connection",
        imageIcon400Url:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-preview/crossword/570d6aeb-0661-44d0-8e5f-0734f6f1d3a6/54d9a4b9-ff87-4472-b7e5-2dbcb57d8c2d.png",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/crossword/570d6aeb-0661-44d0-8e5f-0734f6f1d3a6/c20afb35-226a-4241-ba71-3b131f77fc1d.png",
    },
    plant: {
        storyId: "dd68b36c-6d1c-4bf5-bde4-6d4b58b4ef0d",
        title: "The Tree That Gives Everything",
        imageIcon400Url:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-icon-400/crossword/6066fc61-9898-49dc-a7ac-f04cb62e9334/302e3034-ed00-4c86-a1f6-c91a17fcc612.png",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/crossword/6066fc61-9898-49dc-a7ac-f04cb62e9334/028cec7e-2bbf-4efe-9737-f7ca14196f60.png",
    },
    funny: {
        storyId: "f7215835-1053-4053-ac4a-a52714d97211",
        title: "The Garden's Secret",
        imageIcon400Url:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story-preview/hidden_message/731d8e55-bd37-4e33-a435-cad3f36e508c/d684cc23-7604-45df-910a-22c94eba9398.png",
        imageUrl:
            "https://puzzleinteract-images.s3.us-east-1.amazonaws.com/story/hidden_message/731d8e55-bd37-4e33-a435-cad3f36e508c/30576fb5-7b1b-4d55-bb17-15ce20e6f0a6.png",
    },
};

function snapshotLibraryFixtureKey(tag) {
    const key = String(tag || SNAPSHOT_LIBRARY_SELECTED_TAG).trim().toLowerCase();
    return SNAPSHOT_LIBRARY_FIXTURES[key] ? key : "business";
}

/** Story title for a snapshot library tag (card heading + XCTest markers). */
export function getSnapshotLibraryFixtureStoryTitle(tag) {
    const fixture = SNAPSHOT_LIBRARY_FIXTURES[snapshotLibraryFixtureKey(tag)];
    return fixture?.title || SNAPSHOT_STORY_TITLE;
}

/** Offline library row for Fastlane snapshots (no browse API required). */
export function getSnapshotLibraryFixtureGroup(tag) {
    const fixture = SNAPSHOT_LIBRARY_FIXTURES[snapshotLibraryFixtureKey(tag)];
    return {
        storyId: fixture.storyId,
        story: {
            id: fixture.storyId,
            english: { title: fixture.title },
            en: { title: fixture.title },
            imageIcon400Url: fixture.imageIcon400Url,
            imageUrl: fixture.imageUrl,
        },
        templates: [
            {
                id: SNAPSHOT_SLIDING_TEMPLATE_ID,
                gridSize: 4,
                difficultyLevel: "EASY",
                startingLayout: SNAPSHOT_STARTING_LAYOUT,
            },
        ],
    };
}

/** Play bootstrap payload when native `-PuzzleSnapshotSlidingId` is set. */
export function getSnapshotPlayFixtureBootstrap() {
    return {
        gridSize: 4,
        difficulty: "medium",
        imageUrl: SNAPSHOT_IMAGE_URL,
        previewImageUrl: SNAPSHOT_PREVIEW_URL,
        storyTitle: SNAPSHOT_STORY_TITLE,
        layout: SNAPSHOT_STARTING_LAYOUT,
    };
}

export function difficultyToLevel(difficulty) {
    return DIFF_TO_LEVEL[difficulty] || "MEDIUM";
}

export function levelToDifficulty(level) {
    if (!level) return "medium";
    const key = String(level).toUpperCase();
    return LEVEL_TO_DIFF[key] || "medium";
}

export function isNoData(data) {
    return Boolean(data && typeof data === "object" && data.code === "NO_DATA");
}

export function isFailure(data) {
    return Boolean(data && typeof data === "object" && data.code === "FAILURE");
}

/** Parse API epoch ms (number or ISO string). */
export function parseSessionEpochMs(value) {
    if (value == null) return null;
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n) && n > 0) return n;
    const parsed = Date.parse(String(value));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeSlidingTemplate(raw) {
    if (!raw || typeof raw !== "object") return null;
    const gridSize = Number(raw.gridSize ?? raw.grid_size);
    const startingLayout = raw.startingLayout ?? raw.starting_layout;
    return {
        ...raw,
        id: raw.id ?? null,
        gridSize: Number.isFinite(gridSize) ? gridSize : null,
        difficultyLevel: raw.difficultyLevel ?? raw.difficulty_level ?? null,
        startingLayout: Array.isArray(startingLayout)
            ? startingLayout.map((v) => Number(v))
            : null,
        imageUrl: raw.imageUrl ?? raw.image_url ?? raw.previewImageUrl ?? raw.preview_image_url ?? null,
        state: raw.state ?? null,
        free: raw.free === true,
    };
}

/** Normalize `SlidingPlayRo` (or start/progress response) for the play page. */
export function normalizePlaySession(raw) {
    if (!raw || typeof raw !== "object" || isNoData(raw) || isFailure(raw)) return null;

    const slidingRaw =
        raw.sliding && typeof raw.sliding === "object" ? raw.sliding : null;
    const sliding = normalizeSlidingTemplate(slidingRaw ?? raw);
    const story = raw.story ?? null;
    const slidingId =
        (typeof raw.slidingId === "string" && raw.slidingId.trim()) ||
        sliding?.id ||
        raw.id ||
        null;

    const currentLayout = raw.currentLayout ?? raw.current_layout;
    const layout = Array.isArray(currentLayout)
        ? currentLayout.map((v) => Number(v))
        : sliding?.startingLayout ?? null;

    let gridSize = sliding?.gridSize ?? null;
    if (gridSize == null) {
        const n = Number(raw.gridSize ?? raw.grid_size);
        if (Number.isFinite(n) && n > 0) gridSize = n;
    }
    if (gridSize == null && Array.isArray(layout) && layout.length > 0) {
        const side = Math.round(Math.sqrt(layout.length));
        if (side >= 2) gridSize = side;
    }

    const difficultyLevel =
        sliding?.difficultyLevel ??
        raw.difficultyLevel ??
        raw.difficulty_level ??
        null;

    const imageUrl = pickSlidingPlayImageUrl(story) || sliding?.imageUrl || null;
    const previewImageUrl = pickSlidingOriginalImageUrl(story) || null;
    const finishedAt = parseSessionEpochMs(raw.finishedAt ?? raw.finished_at);
    const isCompleted =
        raw.isCompleted === true ||
        raw.is_completed === true ||
        raw.completed === true ||
        Boolean(finishedAt);

    return {
        id: raw.id ?? null,
        slidingId,
        story,
        storyId: getSlidingGroupStoryId({ story }) ?? raw.storyId ?? null,
        sliding,
        gridSize,
        difficulty: levelToDifficulty(difficultyLevel),
        imageUrl,
        previewImageUrl,
        layout,
        isCompleted,
        storyTitle:
            (typeof raw.storyTitle === "string" && raw.storyTitle.trim()) ||
            getSlidingStoryTitle(story) ||
            undefined,
        startedAt: parseSessionEpochMs(raw.startedAt ?? raw.started_at),
        finishedAt,
        updatedAt: parseSessionEpochMs(raw.updatedAt ?? raw.updated_at),
    };
}

/** `6x6 • Hard` for in-progress card MODE row. */
export function sessionModeLine(session) {
    const gs = Number(session?.gridSize);
    const size = Number.isFinite(gs) && gs > 0 ? `${gs}x${gs}` : "—";
    const diff = DIFFICULTY_LABELS[session?.difficulty] || "—";
    return `${size} • ${diff}`;
}

/** Human-readable play duration for my-puzzles cards. */
export function sessionDurationCaption(session) {
    if (!session) return null;
    const startedAt = session.startedAt;
    const finishedAt = session.finishedAt;

    if (session.isCompleted && startedAt && finishedAt) {
        const duration = UtilService.formatCompletionTime(startedAt, finishedAt);
        return duration ? `Completed in ${duration}` : null;
    }

    if (startedAt && !session.isCompleted) {
        const startMs = parseSessionEpochMs(startedAt);
        if (startMs) {
            const elapsed = Date.now() - startMs;
            if (elapsed > 0) {
                const duration = UtilService.formatHumanize(elapsed);
                return duration ? `Playing for ${duration}` : null;
            }
        }
    }

    if (session.isCompleted && finishedAt) {
        const endMs = parseSessionEpochMs(finishedAt);
        if (endMs) {
            const ago = UtilService.formatHumanizeDetailed(Date.now() - endMs);
            return ago ? `Finished ${ago}` : null;
        }
    }

    return null;
}

/** True when `layout` is a full permutation for `gridSize×gridSize`. */
export function layoutMatchesGrid(layout, gridSize) {
    const gs = Number(gridSize);
    if (!Number.isFinite(gs) || gs <= 0) return false;
    const n = gs * gs;
    return Array.isArray(layout) && layout.length === n;
}

/** Map a play session to the library card `group` shape (single template). */
export function sessionToLibraryGroup(session) {
    if (!session) return null;
    const sliding = session.sliding ?? {};
    const slidingId = session.slidingId || sliding.id;
    if (!slidingId) return null;
    const gs = Number(sliding.gridSize ?? session.gridSize);
    const gridSize = Number.isFinite(gs) && gs > 0 ? gs : session.gridSize ?? null;
    const difficultyLevel =
        sliding.difficultyLevel ??
        difficultyToLevel(session.difficulty) ??
        null;
    const tpl = {
        ...sliding,
        id: slidingId,
        gridSize,
        difficultyLevel,
    };
    if (layoutMatchesGrid(session.layout, gridSize)) {
        tpl.currentLayout = session.layout.map((v) => Number(v));
    }
    return {
        story: session.story,
        storyId: session.storyId,
        templates: [tpl],
    };
}

export function resumeCaption(session) {
    if (!session?.gridSize) return null;
    const diff = DIFFICULTY_LABELS[session.difficulty] || session.difficulty || "Medium";
    return `${session.gridSize}×${session.gridSize} Grid — ${diff}`;
}

/** Puzzle title for the home resume card. */
export function resumeStoryTitle(session) {
    if (!session) return null;
    const fromField = typeof session.storyTitle === "string" ? session.storyTitle.trim() : "";
    if (fromField) return fromField;
    const fromStory = getSlidingStoryTitle(session.story);
    if (fromStory) return fromStory;
    const storyId = session.storyId;
    if (typeof storyId === "string" && storyId.trim()) return storyId.trim();
    return null;
}

/** Build React Router play state from a normalized session. */
export function playNavigateState(session, { reshuffle = false, resume = false } = {}) {
    if (!session?.slidingId) return null;
    const story = session.story ?? null;
    const storyTitle = (session.storyTitle || getSlidingStoryTitle(story) || "").trim();
    const previewImageUrl =
        session.previewImageUrl ?? pickSlidingOriginalImageUrl(story) ?? null;
    return {
        slidingId: session.slidingId,
        gridSize: session.gridSize,
        difficulty: session.difficulty,
        imageUrl: session.imageUrl,
        previewImageUrl,
        layout: session.layout,
        resume: resume && !reshuffle && Array.isArray(session.layout),
        storyTitle: storyTitle || undefined,
    };
}

function filterBedrockTags(list) {
    const bedrockLower = "bedrock";
    return (Array.isArray(list) ? list : []).filter((item) => {
        const tagStr = typeof item === "string" ? item : item?.tag ?? item?.name ?? "";
        return !String(tagStr).toLowerCase().includes(bedrockLower);
    });
}

export function tagLabelFromRow(row) {
    if (row == null) return "";
    if (typeof row === "string") return row.trim();
    const t = row.tag ?? row.name;
    return typeof t === "string" ? t.trim() : "";
}

/** GET /home/sliding/distinct-tags */
export async function fetchDistinctTags(params = {}) {
    const result = await SlidingService.getDistinctTags({
        language: params.language ?? getContentLanguage(),
        state: params.state ?? "PUBLISHED",
    });
    if (!result.success) return [];
    return filterBedrockTags(result.data);
}

export async function fetchBrowsePage(params = {}) {
    const tags = params.tag ? [params.tag] : params.tags;
    return SlidingService.listBrowse({
        ...params,
        tags,
        language: params.language ?? getContentLanguage(),
    });
}

export async function fetchSlidingById(slidingId) {
    const result = await SlidingService.getById(slidingId);
    if (!result.success || !result.data) return null;
    return normalizeSlidingTemplate(result.data);
}

export async function fetchLastGame() {
    // Resume/last-game is an authenticated endpoint; guests have no server session.
    if (!(await AuthService.isAuthenticated())) return null;
    const result = await SlidingService.getLastGame();
    if (!result.success || !result.data) return null;
    return normalizePlaySession(result.data);
}

export async function fetchMyPuzzlesPage(params = {}) {
    return SlidingService.listPlay({
        page: params.page ?? 0,
        pageSize: params.pageSize ?? 20,
        sort: "updatedAt:desc",
        isCompleted: params.isCompleted ?? false,
        onlyStarted: params.onlyStarted ?? true,
    });
}

/** Story + template ids with open play sessions (for hiding from library browse). */
export async function fetchInProgressBrowseKeys() {
    if (!(await AuthService.isAuthenticated())) {
        return { storyIds: new Set(), slidingIds: new Set() };
    }
    try {
        const paged = await fetchMyPuzzlesPage({
            page: 0,
            pageSize: 100,
            isCompleted: false,
            onlyStarted: true,
        });
        const storyIds = new Set();
        const slidingIds = new Set();
        for (const row of paged.content || []) {
            const session = normalizePlaySession(row);
            if (!session || session.isCompleted) continue;
            if (session.slidingId) slidingIds.add(session.slidingId);
            const storyId =
                session.storyId ?? getSlidingGroupStoryId({ story: session.story });
            if (storyId) storyIds.add(storyId);
        }
        return { storyIds, slidingIds };
    } catch (e) {
        Logger.warn("fetchInProgressBrowseKeys", e);
        return { storyIds: new Set(), slidingIds: new Set() };
    }
}

/** Remove library rows the user already started (still shown by browse API — only completed are excluded). */
export function filterBrowseGroupsExcludingInProgress(groups, keys) {
    const list = Array.isArray(groups) ? groups : [];
    const storyIds = keys?.storyIds;
    const slidingIds = keys?.slidingIds;
    if (!storyIds?.size && !slidingIds?.size) {
        return list;
    }
    return list.filter((group) => {
        const storyId = group?.storyId ?? getSlidingGroupStoryId(group);
        if (storyId && storyIds?.has(storyId)) {
            return false;
        }
        const templates = Array.isArray(group?.templates) ? group.templates : [];
        if (templates.some((t) => t?.id && slidingIds?.has(t.id))) {
            return false;
        }
        return true;
    });
}

/** User can still start at least one template in this story group (not in-progress / completed). */
export function groupHasPlayableTemplate(group, keys) {
    if (!group) return false;
    const storyId = group?.storyId ?? getSlidingGroupStoryId(group);
    if (storyId && keys?.storyIds?.has(storyId)) {
        return false;
    }
    if (group.allSlidingsCompletedByUser === true) {
        return false;
    }
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    return templates.some((t) => {
        if (!t?.id) return false;
        if (keys?.slidingIds?.has(t.id)) return false;
        if (t.completedByUser === true) return false;
        return true;
    });
}

/**
 * Browse for a playable template with the given grid + difficulty.
 * @param {number} gridSize
 * @param {string} difficulty
 * @param {{ excludeSlidingId?: string }} [options] Skip a template (e.g. the one just finished).
 */
export async function findTemplateForQuickPlay(gridSize, difficulty, options = {}) {
    const excludeSlidingId =
        typeof options?.excludeSlidingId === "string" ? options.excludeSlidingId.trim() : "";
    const level = difficultyToLevel(difficulty);
    const language = getContentLanguage();
    const keys = (await AuthService.isAuthenticated())
        ? await fetchInProgressBrowseKeys()
        : { storyIds: new Set(), slidingIds: new Set() };

    for (let page = 0; page < 5; page += 1) {
        const paged = await fetchBrowsePage({
            page,
            pageSize: 24,
            language,
            sort: "publishedAt:desc",
            excludeCompleted: true,
        });
        const groups = filterBrowseGroupsExcludingInProgress(paged.content || [], keys).filter((g) =>
            groupHasPlayableTemplate(g, keys),
        );
        for (const group of groups) {
            const templates = (Array.isArray(group?.templates) ? group.templates : []).filter((t) => {
                if (!t?.id) return false;
                if (excludeSlidingId && String(t.id) === excludeSlidingId) return false;
                if (t.completedByUser === true) return false;
                if (keys?.slidingIds?.has(t.id)) return false;
                return true;
            });
            const tpl = templateForGridAndLevel(templates, gridSize, level);
            if (tpl?.id) {
                return {
                    slidingId: tpl.id,
                    template: normalizeSlidingTemplate(tpl),
                    story: group.story,
                    imageUrl:
                        pickSlidingPlayImageUrl(group.story) ||
                        tpl.imageUrl ||
                        tpl.previewImageUrl,
                    gridSize,
                    difficulty,
                    storyTitle: getSlidingStoryTitle(group.story),
                };
            }
        }
        if (!paged.hasNext) break;
    }
    return null;
}

export async function startPlay(slidingId) {
    if (!slidingId) return null;
    const result = await SlidingService.startPlay(slidingId);
    const session = result.success ? normalizePlaySession(result.data) : null;
    if (session) return session;
    const tpl = await fetchSlidingById(slidingId);
    if (!tpl) return null;
    return normalizePlaySession({
        slidingId,
        sliding: tpl,
        currentLayout: tpl.startingLayout,
    });
}

/**
 * First published puzzle from the library browse list (page 0) with a real illustration —
 * used for App Store play screenshots without a hard-coded template id.
 */
export async function findFirstLibrarySnapshotSession() {
    for (let page = 0; page < 3; page += 1) {
        const paged = await fetchBrowsePage({
            page,
            pageSize: 24,
            sort: "publishedAt:desc",
        });
        for (const group of paged.content || []) {
            const templates = Array.isArray(group?.templates) ? group.templates : [];
            for (const tpl of templates) {
                if (!tpl?.id) continue;
                const session = normalizePlaySession({
                    slidingId: tpl.id,
                    sliding: tpl,
                    story: group.story ?? null,
                    currentLayout: tpl.startingLayout ?? tpl.starting_layout,
                });
                if (
                    session?.imageUrl &&
                    typeof session.imageUrl === "string" &&
                    !session.imageUrl.includes("sliding-logo-board")
                ) {
                    return session;
                }
            }
        }
        if (!paged.hasNext) break;
    }
    return null;
}

/**
 * Snapshot / guest play — resolve published template + story images from browse list
 * (no JWT; same data as tapping Play on Library). Falls back to {@link startPlay}.
 */
export async function findSnapshotPlaySession(slidingId) {
    if (!slidingId) return null;
    const target = String(slidingId).trim();

    for (let page = 0; page < 5; page += 1) {
        const paged = await fetchBrowsePage({
            page,
            pageSize: 24,
            sort: "publishedAt:desc",
        });
        for (const group of paged.content || []) {
            const templates = Array.isArray(group?.templates) ? group.templates : [];
            const tpl = templates.find((t) => String(t?.id) === target);
            if (!tpl?.id) continue;
            const session = normalizePlaySession({
                slidingId: tpl.id,
                sliding: tpl,
                story: group.story ?? null,
                currentLayout: tpl.startingLayout ?? tpl.starting_layout,
            });
            if (
                session?.imageUrl &&
                typeof session.imageUrl === "string" &&
                !session.imageUrl.includes("sliding-logo-board")
            ) {
                return session;
            }
        }
        if (!paged.hasNext) break;
    }

    return startPlay(slidingId);
}

/** Build play session from native snapshot bootstrap (`__PUZZLE_SNAPSHOT_PLAY_STATE__`). */
export function buildSnapshotPlaySessionFromBootstrap(slidingId, bootstrap) {
    if (!slidingId || !bootstrap || typeof bootstrap !== "object") return null;
    const layout = Array.isArray(bootstrap.layout) ? bootstrap.layout : null;
    const story = {
        title: bootstrap.storyTitle,
        imageIcon400Url: bootstrap.imageUrl,
        imageUrl: bootstrap.previewImageUrl || bootstrap.imageUrl,
    };
    const session = normalizePlaySession({
        slidingId,
        currentLayout: layout,
        story,
        sliding: {
            id: slidingId,
            gridSize: bootstrap.gridSize,
            difficultyLevel: bootstrap.difficulty,
            startingLayout: layout,
        },
    });
    if (!session?.imageUrl || session.imageUrl.includes("sliding-logo-board")) return null;
    return session;
}

export async function restartPlay(slidingId) {
    if (!slidingId) return null;
    const result = await SlidingService.restartPlay(slidingId);
    return result.success ? normalizePlaySession(result.data) : null;
}

export async function saveProgress(slidingId, { currentLayout, isCompleted, tileMode } = {}) {
    if (!slidingId) return null;
    const result = await SlidingService.progressPlay(slidingId, {
        currentLayout,
        isCompleted,
        tileMode,
    });
    if (!result.success) {
        if (result.alreadyCompleted) {
            return { alreadyCompleted: true };
        }
        console.error(
            `[SlidingPlay] PUT /home/sliding-play/progress failed: ${result.error || "unknown"}`,
        );
        return null;
    }
    return result.data;
}

export async function beginPlayFromSlidingId(slidingId, extras = {}) {
    const session = await startPlay(slidingId);
    if (!session) return { error: "Could not start puzzle. Sign in and try again." };
    if (extras.story && !session.story) session.story = extras.story;
    if (extras.storyTitle && !session.storyTitle) {
        session.storyTitle = extras.storyTitle;
    }
    if (extras.previewImageUrl && !session.previewImageUrl) {
        session.previewImageUrl = extras.previewImageUrl;
    }
    if (extras.imageUrl) session.imageUrl = extras.imageUrl;
    if (extras.gridSize) session.gridSize = extras.gridSize;
    if (extras.difficulty) session.difficulty = extras.difficulty;
    const state = playNavigateState(session);
    if (state && typeof extras.returnPath === "string" && extras.returnPath.trim().startsWith("/")) {
        state.returnPath = extras.returnPath.trim();
    }
    return state ? { state } : { error: "Invalid puzzle data." };
}

/** GET /home/sliding/newest — newest playable story (server skips in-progress + completed when JWT + excludeCompleted). */
export async function fetchNewestSlidingGroup({ language, excludeCompleted = true } = {}) {
    const result = await SlidingService.getNewest({
        language: language ?? getContentLanguage(),
        excludeCompleted,
    });
    if (!result.success || !result.data) return null;
    const group = result.data;
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    if (templates.length === 0 && !group?.story) return null;
    return group;
}

/**
 * Pick a template from a story group by grid size + difficulty.
 * @param {{ skipCompleted?: boolean }} [options] When true, ignore templates with {@code completedByUser}.
 */
export function pickTemplateFromGroup(group, gridSize, difficulty, options = {}) {
    if (!group) return null;
    const skipCompleted = options?.skipCompleted === true;
    const templates = (Array.isArray(group.templates) ? group.templates : []).filter((t) => {
        if (!t?.id) return false;
        if (skipCompleted && t.completedByUser === true) return false;
        return true;
    });
    const level = difficultyToLevel(difficulty);
    const tpl = templateForGridAndLevel(templates, gridSize, level);
    if (!tpl?.id) return null;
    if (skipCompleted && tpl.completedByUser === true) return null;
    const story = group.story ?? null;
    return {
        slidingId: tpl.id,
        template: normalizeSlidingTemplate(tpl),
        story,
        gridSize,
        difficulty,
        imageUrl:
            pickSlidingPlayImageUrl(story) ||
            tpl.imageUrl ||
            tpl.previewImageUrl ||
            null,
        storyTitle: getSlidingStoryTitle(story),
        group,
    };
}

/**
 * Grid thumbnail for library/home cards: play image + selected grid + layout.
 * Uses saved `currentLayout` on in-progress sessions when present.
 * @returns {{ imageUrl: string, gridSize: number, startingLayout: number[] } | null}
 */
export function resolveSlidingBoardPreview(group, gridSize, difficulty) {
    const picked = pickTemplateFromGroup(group, gridSize, difficulty);
    const tpl = picked?.template;
    if (!tpl) return null;
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    const savedLayout = tpl.currentLayout ?? tpl.current_layout;
    const startingLayout = tpl.startingLayout ?? tpl.starting_layout;
    const layout = layoutMatchesGrid(savedLayout, gs)
        ? savedLayout
        : Array.isArray(startingLayout) && startingLayout.length === n
          ? startingLayout
          : null;
    if (!Array.isArray(layout) || layout.length !== n || n <= 0) return null;
    const imageUrl = picked.imageUrl;
    if (!imageUrl || !String(imageUrl).trim()) return null;
    return {
        imageUrl: String(imageUrl).trim(),
        gridSize: gs,
        startingLayout: layout.map((x) => Number(x)),
    };
}

/**
 * Quick / challenge: template from GET /home/sliding/newest when available; else browse scan.
 * Never starts a template the user already completed ({@code completedByUser}).
 */
export async function beginQuickPlay(gridSize, difficulty) {
    const newestGroup = await fetchNewestSlidingGroup({ excludeCompleted: true });
    const fromNewest = pickTemplateFromGroup(newestGroup, gridSize, difficulty, {
        skipCompleted: true,
    });
    if (fromNewest) {
        return beginPlayFromSlidingId(fromNewest.slidingId, {
            imageUrl: fromNewest.imageUrl,
            gridSize: fromNewest.gridSize,
            difficulty: fromNewest.difficulty,
            story: fromNewest.story,
            storyTitle: fromNewest.storyTitle,
            previewImageUrl: pickSlidingOriginalImageUrl(fromNewest.story),
        });
    }

    const found = await findTemplateForQuickPlay(gridSize, difficulty);
    if (!found) {
        return {
            error: `No ${gridSize}×${gridSize} ${DIFFICULTY_LABELS[difficulty] || difficulty} puzzle in the library.`,
        };
    }
    return beginPlayFromSlidingId(found.slidingId, {
        imageUrl: found.imageUrl,
        gridSize: found.gridSize,
        difficulty: found.difficulty,
        story: found.story,
        storyTitle: found.storyTitle,
        previewImageUrl: found.story ? pickSlidingOriginalImageUrl(found.story) : null,
    });
}
